import { ReactElement, useMemo } from 'react';
import { Card, List } from '@mui/material';

import { useCurrentCapacityTypeInfo } from '~hooks/useCurrentCapacityTypeInfo';
import { formmaterSecondToHumanizeTime, getItemValue } from '~utils/index';
import {
  parseCapacityValueWithSuffix,
  getDescriptionCapacity,
} from '~utils/capacityTypes';

import { PlanningIndicatorsProps, PlanningIndicatorColumn } from './types';
import {
  PlanningIndicatorsListSubHeader,
  PlanningIndicatorsListItem,
  PlanningIndicatorsListText,
} from './styles';

const PlanningIndicators = ({
  data,
  isRouteCollect = false,
  isTemplate = false,
}: PlanningIndicatorsProps): ReactElement => {
  const { unit1CapacityTypeId, unit2CapacityTypeId } =
    useCurrentCapacityTypeInfo();

  const indicatorsColumns = useMemo<PlanningIndicatorColumn[]>(
    () => [
      {
        field: 'totalWorkingTime',
        label: 'Tiempo total de servicio',
        formatter: (val) => formmaterSecondToHumanizeTime(val),
      },
      {
        field: 'totalDrivingTime',
        label: 'Tiempo total de manejo',
        formatter: (val) => formmaterSecondToHumanizeTime(val),
      },
      {
        field: 'dayWorkingTotalTime',
        label: 'Jornada de día',
        formatter: (val) => formmaterSecondToHumanizeTime(val),
      },
      {
        field: 'nightWorkingTotalTime',
        label: 'Jornada de noche',
        formatter: (val) => formmaterSecondToHumanizeTime(val),
      },
      {
        field: 'averageOccupancyRateWeight',
        label: `Tasa de ocupación media ${getDescriptionCapacity(
          unit1CapacityTypeId,
        )}`,
        formatter: (val) =>
          parseCapacityValueWithSuffix(val, unit1CapacityTypeId),
      },
      {
        field: 'averageOccupancyRateVolume',
        label: `Tasa de ocupación media ${getDescriptionCapacity(
          unit2CapacityTypeId,
        )}`,
        formatter: (val) =>
          parseCapacityValueWithSuffix(val, unit2CapacityTypeId),
      },
      {
        field: 'rejectOperationsNumber',
        label: 'Paradas rechazadas',
      },
      {
        field: 'routesNumber',
        label: 'Rutas generadas',
      },
    ],
    [unit1CapacityTypeId, unit2CapacityTypeId],
  );

  const title = useMemo(() => {
    let prefix = 'plan de viaje';

    if (isRouteCollect) prefix = 'plan de colecta';

    if (isTemplate) prefix = 'plantilla';

    return `Indicadores de ${prefix}`;
  }, [isRouteCollect, isTemplate]);

  return (
    <Card variant="outlined">
      <List
        subheader={
          <PlanningIndicatorsListSubHeader color="primary">
            {title}
          </PlanningIndicatorsListSubHeader>
        }
        disablePadding
      >
        {indicatorsColumns.map((indicator) => (
          <PlanningIndicatorsListItem
            key={`planning-indicator-${indicator.field}`}
          >
            <PlanningIndicatorsListText
              primary={indicator.label}
              primaryTypographyProps={{
                variant: 'body2',
              }}
              secondary={getItemValue(
                data,
                indicator.field,
                indicator.formatter,
              )}
              secondaryTypographyProps={{
                variant: 'body2',
              }}
            />
          </PlanningIndicatorsListItem>
        ))}
      </List>
    </Card>
  );
};

export default PlanningIndicators;
